import React, { useEffect, useRef } from "react";

/**
 * GalaxyBackground
 * -----------------------------------------------------------------------------
 * Cinematic site-wide cosmic background. One component, mounted once at root.
 *
 * Composition:
 *   Behind content (z:-10) — .galaxy-bg-root:
 *     • .galaxy-space      indigo → jet-black radial base
 *     • .galaxy-milky      diagonal Milky-Way band w/ dust rift
 *     • .galaxy-nebula     colored radial nebulae (parallax on scroll)
 *     • .galaxy-clouds     SVG feTurbulence — real cloud structure
 *     • .galaxy-far        distant faint galaxy smudges
 *     • .galaxy-stars-svg  ~100 tiled dust stars (CSS layer, free)
 *     • <canvas>           three-depth parallax stars w/ glow + shooters
 *   Over content (pointer-events: none):
 *     • .galaxy-tint       colored nebula (soft-light)
 *     • .galaxy-shimmer    overlay sparkle
 *
 * Canvas details:
 *   • 3 depth layers (far / mid / near) each with own parallax factor & size.
 *   • Realistic star color distribution: blue-white (25%), white (55%),
 *     amber (15%), pale red-giant (5%).
 *   • ~5% of stars are BRIGHT and get a pre-rendered radial glow sprite
 *     (drawn once to an offscreen canvas, reused via drawImage — cheap).
 *   • Shooting stars: random angle, spawn every 8–18s, up to 2 concurrent.
 *   • rAF loop, paused on `document.hidden`, honors `prefers-reduced-motion`.
 * -----------------------------------------------------------------------------
 */
export default function GalaxyBackground() {
  const canvasRef = useRef(null);
  const nebulaRef = useRef(null);
  const shimmerRef = useRef(null);
  const milkyRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const nebula = nebulaRef.current;
    if (!canvas || !nebula) return;

    const ctx = canvas.getContext("2d", { alpha: true });
    const reduced =
      typeof window !== "undefined" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const isMobile =
      typeof window !== "undefined" && window.matchMedia("(max-width: 768px)").matches;

    const DPR = Math.min(window.devicePixelRatio || 1, 2);
    let width = 0;
    let height = 0;
    let rafId = 0;
    let hidden = false;
    let nextShoot = performance.now() + 3000;

    // ---------- Star color palette (realistic-ish) ----------
    // [hue, sat%, light%] pairs. Weights control frequency.
    const STAR_COLORS = [
      { w: 55, hue: 210, sat: 12, light: 96 }, // white
      { w: 25, hue: 220, sat: 85, light: 88 }, // blue-white
      { w: 15, hue: 38, sat: 82, light: 82 },  // amber (older G/K stars)
      { w: 5,  hue: 8,  sat: 78, light: 72 },  // pale red giant
    ];
    const STAR_WEIGHT_TOTAL = STAR_COLORS.reduce((a, b) => a + b.w, 0);
    function pickColor() {
      let r = Math.random() * STAR_WEIGHT_TOTAL;
      for (const c of STAR_COLORS) {
        r -= c.w;
        if (r <= 0) return c;
      }
      return STAR_COLORS[0];
    }

    // ---------- Three depth layers ----------
    // parallax = how much the layer shifts with scroll (near > mid > far)
    const LAYERS = [
      { count: isMobile ? 55 : 110, sizeMin: 0.25, sizeMax: 0.9, parallax: 0.02, alpha: 0.75 }, // far
      { count: isMobile ? 30 : 55,  sizeMin: 0.5,  sizeMax: 1.4, parallax: 0.06, alpha: 0.9  }, // mid
      { count: isMobile ? 10 : 22,  sizeMin: 0.9,  sizeMax: 2.3, parallax: 0.14, alpha: 1.0  }, // near (brightest)
    ];
    const stars = []; // { layer, x, y, r, phase, speed, hue, sat, light, bright }
    function seedStars() {
      stars.length = 0;
      LAYERS.forEach((L, li) => {
        for (let i = 0; i < L.count; i++) {
          const c = pickColor();
          const bright = li === 2 && Math.random() < 0.35; // ~30% of near-layer glow
          stars.push({
            li,
            x: Math.random() * width,
            y: Math.random() * height,
            r: L.sizeMin + Math.random() * (L.sizeMax - L.sizeMin),
            phase: Math.random() * Math.PI * 2,
            speed: 0.35 + Math.random() * 0.9,
            hue: c.hue,
            sat: c.sat,
            light: c.light,
            bright,
          });
        }
      });
    }

    // ---------- Pre-rendered glow sprite (offscreen) ----------
    // Drawing radial gradients per-frame per-star is expensive. We render
    // a single soft glow to an offscreen canvas once and drawImage it,
    // tinted per-star via ctx.globalCompositeOperation trick.
    const glowSize = 32;
    const glowCanvas = document.createElement("canvas");
    glowCanvas.width = glowSize * 2;
    glowCanvas.height = glowSize * 2;
    const gctx = glowCanvas.getContext("2d");
    const grad = gctx.createRadialGradient(glowSize, glowSize, 0, glowSize, glowSize, glowSize);
    grad.addColorStop(0, "rgba(255, 255, 255, 1)");
    grad.addColorStop(0.35, "rgba(255, 255, 255, 0.35)");
    grad.addColorStop(1, "rgba(255, 255, 255, 0)");
    gctx.fillStyle = grad;
    gctx.fillRect(0, 0, glowSize * 2, glowSize * 2);

    // ---------- Shooting stars ----------
    const shooters = [];
    function spawnShooter() {
      // Random edge, random angle
      const edge = Math.random();
      let x, y, ang;
      if (edge < 0.5) {
        // top edge, angle points down-right or down-left
        x = Math.random() * width;
        y = -30;
        ang = Math.PI / 2 + (Math.random() - 0.5) * 1.1;
      } else if (edge < 0.75) {
        // left edge
        x = -30;
        y = Math.random() * height * 0.6;
        ang = (Math.random() * 0.6) + 0.15;
      } else {
        // right edge
        x = width + 30;
        y = Math.random() * height * 0.6;
        ang = Math.PI - ((Math.random() * 0.6) + 0.15);
      }
      const speed = 7 + Math.random() * 5;
      shooters.push({
        x, y,
        vx: Math.cos(ang) * speed,
        vy: Math.sin(ang) * speed,
        life: 0,
        maxLife: 70 + Math.floor(Math.random() * 40),
      });
    }

    // ---------- Sizing ----------
    function resize() {
      width = window.innerWidth;
      height = window.innerHeight;
      canvas.width = Math.floor(width * DPR);
      canvas.height = Math.floor(height * DPR);
      canvas.style.width = width + "px";
      canvas.style.height = height + "px";
      ctx.setTransform(DPR, 0, 0, DPR, 0, 0);
      seedStars();
    }
    resize();

    // ---------- Draw ----------
    let scrollY = 0;
    function draw(t) {
      ctx.clearRect(0, 0, width, height);

      // Stars — twinkle + per-layer parallax
      for (let i = 0; i < stars.length; i++) {
        const s = stars[i];
        const L = LAYERS[s.li];
        const twinkle = 0.55 + Math.sin(t * 0.001 * s.speed + s.phase) * 0.45;
        const dy = -scrollY * L.parallax;
        const y = ((s.y + dy) % (height + 40) + (height + 40)) % (height + 40);

        // Draw glow FIRST (behind) only for near-layer bright stars
        if (s.bright) {
          const size = s.r * 14 * twinkle;
          ctx.globalAlpha = 0.55 * twinkle;
          ctx.globalCompositeOperation = "lighter";
          // Tint by drawing a colored rect first via drawImage of grayscale glow
          // + composited color. Simplest: just draw sprite with alpha.
          ctx.drawImage(glowCanvas, s.x - size, y - size, size * 2, size * 2);
          ctx.globalCompositeOperation = "source-over";
          ctx.globalAlpha = 1;
        }

        // Core star point
        ctx.beginPath();
        ctx.fillStyle = `hsla(${s.hue}, ${s.sat}%, ${s.light}%, ${twinkle * L.alpha})`;
        ctx.arc(s.x, y, s.r * (0.7 + twinkle * 0.5), 0, Math.PI * 2);
        ctx.fill();
      }

      // Shooting stars
      for (let i = shooters.length - 1; i >= 0; i--) {
        const sh = shooters[i];
        sh.life++;
        sh.x += sh.vx;
        sh.y += sh.vy;
        const tailLen = 22;
        const tx = sh.x - sh.vx * tailLen;
        const ty = sh.y - sh.vy * tailLen;
        const grad2 = ctx.createLinearGradient(sh.x, sh.y, tx, ty);
        grad2.addColorStop(0, "rgba(255, 244, 224, 0.95)");
        grad2.addColorStop(0.35, "rgba(217, 119, 87, 0.55)");
        grad2.addColorStop(1, "rgba(180, 130, 255, 0)");
        ctx.strokeStyle = grad2;
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.moveTo(sh.x, sh.y);
        ctx.lineTo(tx, ty);
        ctx.stroke();

        // Bright head
        ctx.globalCompositeOperation = "lighter";
        ctx.drawImage(glowCanvas, sh.x - 10, sh.y - 10, 20, 20);
        ctx.globalCompositeOperation = "source-over";

        if (sh.life > sh.maxLife || sh.x < -60 || sh.x > width + 60 || sh.y > height + 60)
          shooters.splice(i, 1);
      }

      // Spawn cadence
      if (!reduced && t > nextShoot && shooters.length < 2) {
        spawnShooter();
        nextShoot = t + 8000 + Math.random() * 10000; // 8–18s
      }
    }

    function loop(t) {
      if (!hidden) draw(t);
      rafId = requestAnimationFrame(loop);
    }

    if (reduced) {
      draw(performance.now());
    } else {
      rafId = requestAnimationFrame(loop);
    }

    // ---------- Parallax (nebula + shimmer + milky way) ----------
    let ticking = false;
    function onScroll() {
      scrollY = window.scrollY || window.pageYOffset || 0;
      if (!ticking) {
        window.requestAnimationFrame(() => {
          const nebulaOffset = isMobile ? 0 : scrollY * -0.08;
          const shimmerOffset = isMobile ? 0 : scrollY * -0.04;
          const milkyOffset = isMobile ? 0 : scrollY * -0.03;
          if (nebula) nebula.style.transform = `translate3d(0, ${nebulaOffset}px, 0)`;
          if (shimmerRef.current)
            shimmerRef.current.style.transform = `translate3d(0, ${shimmerOffset}px, 0)`;
          if (milkyRef.current)
            milkyRef.current.style.transform = `translate3d(0, ${milkyOffset}px, 0)`;
          ticking = false;
        });
        ticking = true;
      }
    }

    function onVisibility() { hidden = document.hidden; }

    window.addEventListener("resize", resize, { passive: true });
    window.addEventListener("scroll", onScroll, { passive: true });
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      cancelAnimationFrame(rafId);
      window.removeEventListener("resize", resize);
      window.removeEventListener("scroll", onScroll);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, []);

  return (
    <>
      {/* Layer 1 · Deep-space base + parallax nebulae (behind everything) */}
      <div aria-hidden="true" data-testid="galaxy-bg-root" className="galaxy-bg-root">
        <div className="galaxy-space" />
        <div ref={milkyRef} className="galaxy-milky" />
        <div ref={nebulaRef} className="galaxy-nebula" />
        <div className="galaxy-clouds" />
        <div className="galaxy-far" />
        <div className="galaxy-stars-svg" />
        <canvas ref={canvasRef} className="galaxy-canvas" />
      </div>

      {/* Layer 2 · Colored nebula tint OVER content (soft-light) */}
      <div aria-hidden="true" data-testid="galaxy-tint" className="galaxy-tint" />

      {/* Layer 3 · Faint shimmer OVER content so every page picks up cosmic dust */}
      <div
        aria-hidden="true"
        ref={shimmerRef}
        data-testid="galaxy-shimmer"
        className="galaxy-shimmer"
      />
    </>
  );
}
